/* Feb-2026 · Site-wide Re-Sign v1.1 banner
 *
 * Renders a discreet top-of-page banner whenever the signed-in author
 * has an outdated `contract_version` on their `author_agreements`
 * row. Clicking "Review & Sign" navigates to the Business Dashboard
 * where AuthorAgreementCard already handles the full sign flow.
 *
 * Silent when:
 *   • user is not authenticated
 *   • agreement is null (never signed → they'll be onboarded elsewhere)
 *   • agreement is current
 *   • user dismissed the banner during this browser session
 *
 * Backend: GET /api/author-agreements/me — the same endpoint the
 * AuthorAgreementCard already consumes, so no new API required.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ScrollText, X } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

const DISMISS_KEY = 'resign_banner_dismissed_v1';

export default function ResignBanner() {
  const [state, setState] = useState(null); // 'show' | 'hide' | null
  const [me, setMe] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Don't render the banner on landing / login / signup / bookstore.
  const hiddenPaths = ['/', '/login', '/signup', '/reset'];
  const onHidden = hiddenPaths.some(
    (p) => location.pathname === p || location.pathname.startsWith(`${p}/`),
  ) || location.pathname.startsWith('/bookstore');

  useEffect(() => {
    if (onHidden) { setState('hide'); return; }
    if (sessionStorage.getItem(DISMISS_KEY) === '1') { setState('hide'); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await http.get('/author-agreements/me');
        if (cancelled) return;
        const agreement = r.data?.agreement;
        const needs = agreement && r.data?.current_version &&
          agreement.contract_version && agreement.contract_version !== r.data.current_version;
        if (needs) {
          setMe({
            current_version: r.data.current_version,
            signed_version:  agreement.contract_version,
          });
          setState('show');
        } else {
          setState('hide');
        }
      } catch {
        setState('hide');
      }
    })();
    return () => { cancelled = true; };
  }, [onHidden, location.pathname]);

  if (state !== 'show' || !me) return null;

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setState('hide');
  };

  const go = () => {
    navigate('/app/business');
  };

  return (
    <div
      className="sticky top-0 z-40 flex items-center gap-3 border-b border-amber-300 bg-amber-50 px-4 py-2 text-[12.5px] text-amber-900 shadow-sm"
      data-testid="resign-banner"
    >
      <ScrollText className="h-4 w-4 shrink-0" />
      <div className="flex-1">
        <span className="font-semibold">Contract update available.</span>{' '}
        You're on <span className="font-mono">{me.signed_version}</span>. Please review and sign{' '}
        <span className="font-mono">{me.current_version}</span> so the new terms apply to your future royalties.
      </div>
      <Button size="sm" onClick={go} data-testid="resign-banner-cta" className="h-7 px-3 text-[12px]">
        Review &amp; Sign
      </Button>
      <button
        onClick={dismiss}
        className="rounded-md p-1 text-amber-800 hover:bg-amber-100"
        aria-label="Dismiss"
        data-testid="resign-banner-dismiss"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
